import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

export type CarrierPortalRole =
  | "CARRIER_OWNER"
  | "CARRIER_DISPATCHER"
  | "OPERATOR";
export type EditableCarrierPortalRole = Exclude<CarrierPortalRole, "OPERATOR">;
export type CarrierPortalMembershipStatus = "ACTIVE" | "DISABLED";

export interface InviteCarrierPortalUserBody {
  email: string;
  role: EditableCarrierPortalRole;
  dispatchCenterId?: string | null;
}

export interface InviteCarrierPortalUserResponse {
  invite: {
    id: string;
    email: string;
    carrierId: string;
    dispatchCenterId: string | null;
    role: EditableCarrierPortalRole;
    token: string;
    invitedByUserId: string | null;
    invitedByAdminUserId: string | null;
    expiresAt: string;
    acceptedAt: string | null;
    acceptedByCarrierUserId: string | null;
    createdAt: string;
    carrier: { id: string; name: string };
    dispatchCenter: { id: string; name: string } | null;
  };
}

/**
 * Admin sends a Carrier Admin or Dispatcher portal invite to a partner carrier.
 * The carrier row must already exist (use `useCreateCarrier()` first if needed).
 */
export function useInviteCarrierPortalUser(options?: {
  onSuccess?: (data: InviteCarrierPortalUserResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      body,
    }: {
      carrierId: string;
      body: InviteCarrierPortalUserBody;
    }) => {
      const { data } = await axios.post<InviteCarrierPortalUserResponse>(
        `${API_URL}/api/v2/admin/partner-carriers/${carrierId}/invites`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-portal-team", vars.carrierId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export interface CarrierPortalMembership {
  id: string;
  carrierId: string;
  dispatchCenterId: string | null;
  role: CarrierPortalRole;
  status: CarrierPortalMembershipStatus;
  createdAt: string;
  updatedAt: string;
  disabledAt: string | null;
  disabledByCarrierUserId: string | null;
  disabledByInternalUserId: string | null;
  carrier: { id: string; name: string };
  dispatchCenter: { id: string; name: string } | null;
}

export interface CarrierPortalMember {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  active: boolean;
  createdAt: string;
  memberships: CarrierPortalMembership[];
}

export interface CarrierPortalInvite {
  id: string;
  email: string;
  role: CarrierPortalRole;
  carrierId: string;
  dispatchCenterId: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedByCarrierUserId: string | null;
  invitedByAdminUserId: string | null;
  invitedByUserId: string | null;
  carrier: { id: string; name: string };
  dispatchCenter: { id: string; name: string } | null;
}

export interface CarrierPortalTeamResponse {
  carrier: { id: string; code: string; name: string };
  members: CarrierPortalMember[];
  invites: CarrierPortalInvite[];
}

export function useGetCarrierPortalTeam(carrierId: string | null) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-portal-team", carrierId],
    queryFn: async () => {
      const { data } = await axios.get<{ data: CarrierPortalTeamResponse }>(
        `${API_URL}/api/v2/carriers/${carrierId}/team`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

export function useUpdateCarrierPortalMember(options?: {
  onSuccess?: (data: CarrierPortalMember) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      carrierUserId,
      body,
    }: {
      carrierId: string;
      carrierUserId: string;
      body: { active: boolean };
    }) => {
      const { data } = await axios.patch<{ data: CarrierPortalMember }>(
        `${API_URL}/api/v2/carriers/${carrierId}/team/members/${carrierUserId}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-portal-team", vars.carrierId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateCarrierPortalMembership(options?: {
  onSuccess?: (data: CarrierPortalMembership) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      membershipId,
      body,
    }: {
      carrierId: string;
      membershipId: string;
      body: {
        role?: EditableCarrierPortalRole;
        dispatchCenterId?: string | null;
        status?: CarrierPortalMembershipStatus;
      };
    }) => {
      const { data } = await axios.patch<{ data: CarrierPortalMembership }>(
        `${API_URL}/api/v2/carriers/${carrierId}/team/memberships/${membershipId}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-portal-team", vars.carrierId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteCarrierPortalInvite(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      inviteId,
    }: {
      carrierId: string;
      inviteId: string;
    }) => {
      await axios.delete(
        `${API_URL}/api/v2/carriers/${carrierId}/team/invites/${inviteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-portal-team", vars.carrierId],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useResendCarrierPortalInvite(options?: {
  onSuccess?: (data: CarrierPortalInvite) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      inviteId,
    }: {
      carrierId: string;
      inviteId: string;
    }) => {
      const { data } = await axios.post<{ data: CarrierPortalInvite }>(
        `${API_URL}/api/v2/carriers/${carrierId}/team/invites/${inviteId}/resend`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-portal-team", vars.carrierId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
